import React, { useEffect, useMemo, useRef, useState } from 'react';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';

interface PasswordBlurProps {
    value?: string;
    initiallyHidden?: boolean;
    className?: string;
}

const PasswordBlur: React.FC<PasswordBlurProps> = ({ value = '', initiallyHidden = true, className = '' }) => {
    const [hidden, setHidden] = useState(initiallyHidden);
    const [displayText, setDisplayText] = useState('');
    const [showTooltip, setShowTooltip] = useState(false);
    const animRef = useRef<number | null>(null);
    const tooltipTimerRef = useRef<number | null>(null);

    const randomChars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';

    const makeMask = useMemo(() => {
        return () => {
            const length = 10;
            let out = '';
            for (let i = 0; i < length; i++) {
                out += randomChars[Math.floor(Math.random() * randomChars.length)];
            }
            return out;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!value) return;
        if (hidden) {
            setDisplayText(makeMask());
            if (animRef.current) cancelAnimationFrame(animRef.current);
            animRef.current = null;
            if (tooltipTimerRef.current) {
                clearTimeout(tooltipTimerRef.current);
                tooltipTimerRef.current = null;
            }
            setShowTooltip(false);
            return;
        }

        // Matrix-like reveal over ~1s
        const durationMs = 500;
        const start = performance.now();
        const target = value;

        const step = (now: number) => {
            const elapsed = now - start;
            const progress = Math.min(1, elapsed / durationMs);
            const revealCount = Math.floor(progress * target.length);

            let next = '';
            for (let i = 0; i < target.length; i++) {
                if (i < revealCount) {
                    next += target[i];
                } else {
                    next += randomChars[Math.floor(Math.random() * randomChars.length)];
                }
            }
            setDisplayText(next);

            if (progress < 1) {
                animRef.current = requestAnimationFrame(step);
            } else {
                animRef.current = null;
                setDisplayText(target);
                // copy to clipboard and show tooltip for 2s
                try {
                    void navigator.clipboard.writeText(target);
                } catch (e) {
                    // ignore
                }
                setShowTooltip(true);
                if (tooltipTimerRef.current) clearTimeout(tooltipTimerRef.current);
                tooltipTimerRef.current = window.setTimeout(() => {
                    setShowTooltip(false);
                    tooltipTimerRef.current = null;
                }, 2000);
            }
        };

        animRef.current = requestAnimationFrame(step);

        return () => {
            if (animRef.current) cancelAnimationFrame(animRef.current);
            animRef.current = null;
            if (tooltipTimerRef.current) {
                clearTimeout(tooltipTimerRef.current);
                tooltipTimerRef.current = null;
            }
        };
    }, [hidden, value]);

    if (!value) {
        return <span>-</span>;
    }

    return (
        <div className="relative">
            <div className={`inline-flex w-[180px] border justify-between rounded-md bg-white p-1.5 items-center gap-2 ${className}`}>
                <span className={hidden ? 'blur-sm' : ''}>{displayText}</span>
                <button
                    type="button"
                    onClick={() => setHidden((prev) => !prev)}
                    aria-label={hidden ? 'show' : 'hide'}
                    title={hidden ? 'show' : 'hide'}
                >
                    {hidden ? <EyeIcon className="size-4" /> : <EyeSlashIcon className="size-4" />}
                </button>
            </div>
            {showTooltip && (
                <div className="mt-1 absolute -bottom-4 w-[180px] rounded-b-md text-center bg-[#52A445] left-0 text-[#fff] font-bold text-[10px]">In die Zwischenablage kopiert</div>
            )}
        </div>
    );
};

export default PasswordBlur;



